import useInfiniteService from '@/data/hook/useInfiniteService';
import useToggle from '@/data/hook/useToggle';
import { courseModulesQueryKeys } from '@/data/services/querykeys';
import { CourseLine } from '@/models/CourseLine';
import { useTranslation } from 'react-i18next';
import CourseModuleTree from '@/components/module/CourseModuleTree';
import CoursesTreeList from '@/components/shared/CoursesTreeList';
import TreeAddButton from '@/components/shared/TreeAddButton';
import TreeItem, { TreeItemProps } from '@/components/shared/TreeItem';
import { mountKey } from '@/utils/courses';
import { CoursesModelsType } from '@/utils/trees/TreeCourses';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import { ROUTES } from '@/utils/routes';

export default function CourseLinesTree({
  item: courseLine,
  parentId,
}: TreeItemProps<CourseLine>) {
  const type: CoursesModelsType = 'courseLine';
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.tree',
  });
  const { navigate } = useCourseTreeNavigate();
  const key = mountKey({ type, id: courseLine.id });

  const { isOpen, toggle, open } = useToggle();

  const {
    results: courseModules,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading,
  } = useInfiniteService({
    ...courseModulesQueryKeys.list(courseLine.id)._ctx.infinity,
    enabled: isOpen,
  });

  return (
    <TreeItem
      objKey={key}
      item={courseLine}
      parentId={parentId}
      type={type}
      isLoading={isInitialLoading}
      open={open}
      onToggle={toggle}
      isOpen={isOpen}
    >
      <CoursesTreeList
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isInitialLoading}
        onReachEnd={fetchNextPage}
        hasNextPage={hasNextPage}
      >
        {courseModules.map(courseModule => (
          <CourseModuleTree
            key={mountKey({ type: 'module', id: courseModule.id })}
            item={courseModule}
            parentId={key}
          />
        ))}
        <TreeAddButton
          label={t('newModule')}
          onClick={() =>
            navigate(ROUTES.ADMIN.COURSE_LINES.MODULE(courseLine.id, 'new'))
          }
        />
      </CoursesTreeList>
    </TreeItem>
  );
}
